import * as swcHelpers from '@swc/helpers';
import { takeLatest, select, put, call } from 'redux-saga/effects';
import { Customers, Products } from '@grandvisionhq/api-client';
import { Selectors, State, getToken } from '@grandvisionhq/auth';
import { toast } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { IntlSelectors, failedState, successState } from '@grandvisionhq/state';
import { FETCH_DETAIL, PAUSE, PAUSE_SUCCESS, QUANTITY, QUANTITY_FAILED, QUANTITY_SUCCESS, FETCH_PRODUCT_DATA, FETCH_LIST, CCC_ADDRESS_SAVE, fetchDetailFailed, fetchDetailSuccess, fetchProductData, pauseFailed, pauseSuccess, fetchDetail, quantityFailed, quantitySuccess, fetchProductDataFailed, fetchProductDataSuccess, fetchListFailed, fetchListSuccess, saveCCCAddressFailed, saveCCCAddressSuccess } from './actions.js';
import { createProductLookup } from './create-product-lookup.js';
import { getCCCSubscriptionDetail } from './selectors.js';
import { getProductTypeName, getCCCVisualProduct } from './utils/get-ccc-visual-product.js';

function* onDetailFetch(action) {
    const auth = yield select(Selectors.getAuth);
    if (!State.isLoggedIn(auth)) {
        yield put(fetchDetailFailed(new Error(`Can't fetch subscription details when user isn't logged-in`)));
        return;
    }
    try {
        const token = yield call(getToken);
        const response = yield call(Customers.CCC.details, auth.apiUrl, token, {
            customerId: auth.userId,
            subscriptionId: action.payload.cccSubscriptionId
        });
        yield put(fetchDetailSuccess(response));
        const productIds = response.lineItems.map((i)=>i.productId
        );
        yield put(fetchProductData({
            productIds
        }));
    } catch (err) {
        yield put(fetchDetailFailed(err));
    }
}
function* onPause(action) {
    const auth = yield select(Selectors.getAuth);
    if (!State.isLoggedIn(auth)) {
        yield put(pauseFailed(new Error(`Can't update subscription pausing state when user isn't logged-in`)));
        return;
    }
    const detail = yield select(getCCCSubscriptionDetail);
    if (detail) {
        try {
            const token = yield call(getToken);
            yield call(Customers.CCC.pause, auth.apiUrl, token, auth.userId, detail.id, action.payload);
            yield put(pauseSuccess({
                cccSubscriptionId: detail.id
            }));
        } catch (err) {
            yield put(pauseFailed(err));
        }
    }
}
function* onPauseSuccess(action) {
    const { cccSubscriptionId  } = action.payload;
    yield put(fetchDetail({
        cccSubscriptionId
    }));
    yield call(toast.success, 'account.ccc.pause.success.toast');
}
function* onQuantity(action) {
    const auth = yield select(Selectors.getAuth);
    if (!State.isLoggedIn(auth)) {
        yield put(quantityFailed(new Error(`Can't update subscription quantity when user isn't logged-in`)));
        return;
    }
    const detail = yield select(getCCCSubscriptionDetail);
    if (detail) {
        const { quantity  } = action.payload;
        const subscriptionQuantity = detail.lineItems.reduce((q, i)=>i.quantity
        , 0);
        if (quantity === subscriptionQuantity) {
            yield put(quantityFailed({
                reason: 'quantity_same'
            }));
            return;
        }
        try {
            const token = yield call(getToken);
            yield call(Customers.CCC.quantity, auth.apiUrl, token, auth.userId, detail.id, action.payload);
            yield put(quantitySuccess({
                cccSubscriptionId: detail.id
            }));
        } catch (err) {
            yield put(quantityFailed(err));
        }
    }
}
function* onQuantitySuccess(action) {
    const { cccSubscriptionId  } = action.payload;
    yield put(fetchDetail({
        cccSubscriptionId
    }));
    yield call(toast.success, 'account.ccc.quantity.success.toast');
}
function* onQuantityFailed(action) {
    if ('reason' in action.payload) {
        yield call(toast.info, 'account.ccc.quantity.noChange');
    }
}
function* onFetchProductData(action) {
    const { productIds  } = action.payload;
    const locale = yield select(IntlSelectors.getLocale);
    const auth = yield select(Selectors.getAuth);
    try {
        const { results  } = yield call(Products.query, `${auth.apiUrl}/products`, {
            filters: [
                `variants.sku:${encodeURIComponent(productIds.map((id)=>`"${id}"`
                ).join(','))}`
            ],
            expand: [
                'productType'
            ]
        });
        if (!results || !results.length) {
            const productData = productIds.reduce((acc, id)=>swcHelpers.objectSpread({}, acc, {
                    [id]: failedState(new Error('Did not receive any product data for this product'))
                })
            , {});
            yield put(fetchProductDataFailed({
                error: new Error(`Did not receive any product data for these IDs: ${productIds.join(', ')}`),
                productData
            }));
        }
        const lookup = createProductLookup(results);
        let succeeded = {};
        let failed = {};
        productIds.forEach((id)=>{
            const product = lookup.product[id];
            const variant = lookup.variant[id];
            if (!product || !variant) {
                failed = swcHelpers.objectSpread({}, failed, {
                    [id]: failedState(new Error(`No data was returned for variant with ID ${id}`))
                });
                return;
            }
            const productTypeName = getProductTypeName(product);
            if (!productTypeName) {
                failed = swcHelpers.objectSpread({}, failed, {
                    [id]: failedState(new Error(`Couldn't find productType from product with ID ${product.id} for SKU ${id}`))
                });
            }
            if (id in lookup.product && id in lookup.variant && productTypeName) {
                const info = getCCCVisualProduct(product, productTypeName, locale || 'en-IE', variant);
                succeeded = swcHelpers.objectSpread({}, succeeded, {
                    [id]: successState(info)
                });
            } else {
                failed = swcHelpers.objectSpread({}, failed, {
                    [id]: failedState(new Error(`No data was returned for variant with ID ${id}`))
                });
            }
        });
        if (Object.keys(succeeded).length > 0) {
            yield put(fetchProductDataSuccess({
                productData: succeeded
            }));
        }
        if (Object.keys(failed).length > 0) {
            const error = new Error(`Encountered errors for variants with the following IDs: ${Object.keys(failed).join(', ')}`);
            yield put(fetchProductDataFailed({
                error,
                productData: failed
            }));
        }
    } catch (error) {
        const productData = productIds.reduce((acc, id)=>({
                [id]: failedState(new Error(`Encountered an error when fetching data. Error: ${error}`))
            })
        , {});
        yield put(fetchProductDataFailed({
            error: error,
            productData
        }));
    }
}
function* onFetchList() {
    const auth = yield select(Selectors.getAuth);
    if (!State.isLoggedIn(auth)) {
        yield put(fetchListFailed(new Error(`Can't fetch subscription list when user isn't logged-in`)));
        return;
    }
    try {
        const accessToken = yield call(getToken);
        const result = yield call(Customers.CCC.list, auth.apiUrl, {
            customerId: auth.userId,
            accessToken
        });
        yield put(fetchListSuccess(result.subscriptions));
    } catch (err) {
        yield put(fetchListFailed(err));
    }
}
function* onSaveCCCAddress(action) {
    const auth = yield select(Selectors.getAuth);
    if (!State.isLoggedIn(auth)) {
        yield put(saveCCCAddressFailed(new Error(`Can't update subscription address when user isn't logged-in`)));
        return;
    }
    try {
        const accessToken = yield call(getToken);
        const { custom  } = yield select(getCCCSubscriptionDetail);
        yield call(Customers.changeAddress, auth.apiUrl, accessToken, auth.userId, {
            fields: Util.getValuesFromState(action.payload),
            externalId: custom === null || custom === void 0 ? void 0 : custom.membershipId,
            addressTypes: [
                'delivery-subscription'
            ]
        });
        yield put(saveCCCAddressSuccess());
    } catch (err) {
        yield put(saveCCCAddressFailed(err));
    }
}
var cccSagas = [
    takeLatest(FETCH_DETAIL, onDetailFetch),
    takeLatest(PAUSE, onPause),
    takeLatest(PAUSE_SUCCESS, onPauseSuccess),
    takeLatest(QUANTITY, onQuantity),
    takeLatest(QUANTITY_FAILED, onQuantityFailed),
    takeLatest(QUANTITY_SUCCESS, onQuantitySuccess),
    takeLatest(FETCH_PRODUCT_DATA, onFetchProductData),
    takeLatest(FETCH_LIST, onFetchList),
    takeLatest(CCC_ADDRESS_SAVE, onSaveCCCAddress), 
];

export { cccSagas as default, onDetailFetch, onFetchList, onFetchProductData, onPause, onPauseSuccess, onQuantity, onQuantityFailed, onQuantitySuccess, onSaveCCCAddress };
//# sourceMappingURL=sagas.js.map
